export interface PartnerLandingLogo {
    src: string;
    alt: string;
    key: string;
}

/**
 * Allowlisted `utm_*` keys for partner short-link expansion, session marketing context, and login payloads.
 * Must stay aligned with API / marketing attribution.
 */
export const PARTNER_RULE_UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_external_source',
] as const;

export type PartnerLandingRuleUtmKey = (typeof PARTNER_RULE_UTM_KEYS)[number];

/** Max trimmed string length per key when persisting or validating partner-rule `utm` objects. */
export const PARTNER_RULE_UTM_MAX_LEN: Record<PartnerLandingRuleUtmKey, number> = {
    utm_source: 128,
    utm_medium: 128,
    utm_campaign: 256,
    utm_content: 256,
    utm_term: 256,
    utm_external_source: 128,
};

export type PartnerLandingBrandingRuleUtm = Partial<Record<PartnerLandingRuleUtmKey, string>>;

export interface PartnerLandingBrandingRule {
    partner?: string;
    feature?: string;
    /** Optional short `?c=` code; resolved from bundled partner branding, not the DB. Max 32 chars; `[a-z0-9_-]`. */
    publicLinkCode?: string;
    /**
     * Optional external source id (UUID string) emitted as `utm_external_source` on campaign redirect.
     * When set, `resolvePartnerLandingBranding` requires the same id from `utm_external_source` on the URL.
     */
    externalSourceId?: string;
    /** Allowlisted `utm_*` for this rule’s `?c=` redirect — sole source for short-link expansion (API + SPA). */
    utm: PartnerLandingBrandingRuleUtm;
    taglineSuffix: string;
    partnerLogo?: PartnerLandingLogo;
}

export interface PartnerLandingBrandingConfig {
    schemaVersion: 1;
    defaultTaglineSuffix: string;
    kuderLogo: PartnerLandingLogo;
    rules: PartnerLandingBrandingRule[];
}

/** Parsed from URL for partner landing (`utm_source=kuder` + optional content/term / external source). */
export interface PartnerLandingAttribution {
    /** True when `utm_source` normalizes to `kuder` (see partnerLandingUrl). */
    isKuderSource: boolean;
    /** Partner slug from `utm_content` (optional). */
    partner?: string;
    /** Feature slug from `utm_term` (optional). */
    feature?: string;
    /** External source id from `utm_external_source` (optional; UUID string, not slug-normalized). */
    externalSourceId?: string;
}

export interface PartnerLandingBranding {
    taglineSuffix: string;
    logos: PartnerLandingLogo[];
}
